'use client';

import classNames from 'classnames';
import {
  type FocusEvent,
  type ForwardedRef,
  forwardRef,
  type KeyboardEvent,
  useState
} from 'react';

interface Props {
  warning?: boolean;
  label?: string;
  value: string | number;
  onChange?: (value: string) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => false | Promise<void>;
  onBlur?: (
    e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => Promise<void>;
  type?:
    | 'text'
    | 'password'
    | 'email'
    | 'number'
    | 'search'
    | 'url'
    | 'textarea';
  disabled?: boolean;
  autofocus?: boolean;
  name?: string;
  className?: string;
  icons?: React.ReactNode[];
  inputClassName?: string;
  onFocus?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
  /** Strip borders, ring, and floating label. Input becomes flush with its container. */
  borderless?: boolean;
  /** Number of rows for textarea. Defaults to 3 (borderless) or 5. */
  rows?: number;
  /** Simple placeholder text (independent of floating label). */
  placeholder?: string;
  /** HTML autocomplete token(s). Defaults to "off". Use "username webauthn" to
   * surface passkeys in the field's native autofill dropdown. */
  autoComplete?: string;
  /** DOM id. Defaults to the label, which is what tests select on — pass one
   * when the label's wording should be free to change without moving the id. */
  id?: string;
}

const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      warning = false,
      label,
      value,
      onChange,
      onKeyDown,
      onBlur,
      type = 'text',
      disabled,
      autofocus,
      name,
      className,
      icons,
      inputClassName,
      onFocus,
      textareaRef,
      borderless = false,
      rows: rowsProp,
      placeholder,
      autoComplete = 'off',
      id
    },
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const shouldUseFloating = Boolean(label) && !borderless;
    const hasValue = value !== '' && value !== undefined && value !== null;
    // With no placeholder the label doubles AS the placeholder, so it only
    // floats once there is something to make room for. Give the field its own
    // placeholder and that trick is gone — the label has to float from the
    // start or an empty field shows a hint and no name at all.
    const showTopLabel =
      shouldUseFloating && (isFocused || hasValue || Boolean(placeholder));
    const effectivePlaceholder = placeholder
      ? placeholder
      : borderless
        ? label || ''
        : shouldUseFloating && !showTopLabel
          ? label!
          : '';

    const handleFocus = (
      e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      setIsFocused(true);
      if (onFocus) onFocus(e);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      if (onBlur) void onBlur(e);
    };

    const fieldsetClass = classNames(
      'group min-w-0 rounded-md px-3 pb-0 pt-0 transition-colors duration-300 ease-in-out',
      warning
        ? 'border border-destructive focus-within:border-destructive'
        : 'border border-ds-border-standard focus-within:border-primary',
      className
    );

    // Floated label. The legend stays mounted whenever there's a label (it never
    // toggles on focus), so it always reserves the same vertical space; with the
    // constant input padding below that keeps the field height fixed (no jump on
    // focus). Unfloated, its width collapses to 0 so it cuts no border notch and
    // the label shows as the placeholder instead.
    const legendClass = classNames(
      'ml-2 overflow-hidden whitespace-nowrap text-sm font-emphasis leading-none transition-all duration-200 ease-in-out',
      showTopLabel ? 'max-w-full px-0.5 opacity-100' : 'max-w-0 opacity-0',
      warning
        ? 'text-destructive'
        : 'text-textSecondary group-focus-within:text-primary'
    );

    if (borderless) {
      return (
        <div
          className={`flex w-full flex-col justify-center align-middle ${className || ''}`}
        >
          {type === 'textarea' ? (
            <textarea
              ref={textareaRef}
              className="w-full resize-none bg-transparent px-3 py-2 text-base text-textSecondary focus-visible:outline-none"
              rows={rowsProp ?? 3}
              id={id ?? label}
              placeholder={placeholder ?? label ?? ''}
              autoComplete="off"
              value={value}
              onChange={onChange ? (e) => onChange(e.target.value) : undefined}
              onFocus={handleFocus}
              onBlur={(e) => {
                setIsFocused(false);
                if (onBlur)
                  void onBlur(e as unknown as FocusEvent<HTMLInputElement>);
              }}
              disabled={disabled}
              autoFocus={autofocus}
              name={name}
            />
          ) : (
            <input
              onKeyDown={onKeyDown}
              onFocus={handleFocus}
              className={classNames(
                'w-full truncate bg-transparent px-3 py-2 text-base focus-visible:outline-none',
                inputClassName
              )}
              type={type}
              id={id ?? label}
              placeholder={placeholder ?? label ?? ''}
              autoComplete="off"
              value={value}
              onChange={onChange ? (e) => onChange(e.target.value) : undefined}
              onBlur={handleBlur}
              disabled={disabled}
              autoFocus={autofocus}
              name={name}
              ref={ref}
            />
          )}
        </div>
      );
    }

    return (
      <fieldset className={fieldsetClass}>
        {label && <legend className={legendClass}>{label}</legend>}
        {type !== 'textarea' && (
          <div className="relative">
            <input
              onKeyDown={onKeyDown}
              onFocus={handleFocus}
              style={
                icons
                  ? {
                      paddingRight: `${icons.length * 20 + 16 + (icons.length - 1) * 10}px`
                    }
                  : {}
              }
              className={classNames(
                `w-full truncate bg-transparent pb-3 pt-0 text-base focus-visible:outline-none ${warning ? 'text-destructive' : ''}`,
                inputClassName
              )}
              type={type}
              id={id ?? label}
              placeholder={effectivePlaceholder}
              autoComplete={autoComplete}
              value={value}
              onChange={onChange ? (e) => onChange(e.target.value) : undefined}
              onBlur={handleBlur}
              disabled={disabled}
              autoFocus={autofocus}
              name={name}
              ref={ref}
            />
            {icons && (
              <div className="absolute right-0 top-0 flex h-full flex-row items-center justify-center gap-x-[10px] pr-1">
                {icons}
              </div>
            )}
          </div>
        )}
        {type === 'textarea' && (
          <textarea
            ref={textareaRef}
            className={classNames(
              `w-full resize-none bg-transparent pb-3 pt-0 text-base text-textSecondary focus-visible:outline-none ${warning ? 'text-destructive' : ''}`,
              inputClassName
            )}
            rows={rowsProp ?? 5}
            id={id ?? label}
            placeholder={
              placeholder
                ? placeholder
                : shouldUseFloating && !showTopLabel
                  ? label!
                  : ''
            }
            autoComplete="off"
            value={value}
            onChange={onChange ? (e) => onChange(e.target.value) : undefined}
            onFocus={handleFocus}
            onBlur={(e) => {
              setIsFocused(false);
              if (onBlur) void onBlur(e);
            }}
            disabled={disabled}
            autoFocus={autofocus}
            name={name}
          />
        )}
      </fieldset>
    );
  }
);

Input.displayName = 'Input';

export default Input;
